import dayjs from 'dayjs';
import { JobPublication as JobPublicationApi } from '../../dvinci-api/types/job-publication';
import JobOpening, { createJobOpening } from './job-opening';

export default interface Job {
  id: string;
  language: string;
  position: string;
  pageTitle: string;
  subtitle?: string;
  jobPublicationURL?: string;
  applicationFormURL: string;
  applicationApplyApiURL?: string;
  startDate?: Date;
  endDate?: Date;
  introduction?: string;
  tasks?: string;
  profile?: string;
  weOffer?: string;
  closingText?: string;
  images?: Image[];
  renderedContent?: RenderedContent;
  jobOpening: JobOpening;
  pageDescription?: string;
  benefits?: Benefit[];
  sortDate: Date;
  keywords: string[];
  search: string;
}

export interface RenderedContent {
  contact: { body: string; headline: string };
  introduction: { body: string; headline: string };
  position: { body: string };
  profile: { body: string; headline: string };
  tasks: { body: string; headline: string };
  weOffer: { body: string; headline: string };
  additionalInformation: { body: string };
}

export interface Benefit {
  icon: string;
  internalName: string;
  name: string;
  description?: string;
}

export interface Image {
  type: ImageType;
  url: string;
  additionalImages?: { url: string }[];
}

export type ImageType =
  | 'JOB_PUBLICATION_PAGE_TOP_LEFT'
  | 'JOB_PUBLICATION_PAGE_TOP_CENTER'
  | 'JOB_PUBLICATION_PAGE_TOP_RIGHT'
  | 'JOB_PUBLICATION_PAGE_PANORAMA'
  | 'JOB_PUBLICATION_PAGE_BOTTOM_CENTER'
  | 'JOB_PUBLICATION_PAGE_BOTTOM_RIGHT'
  | 'BACKGROUND'
  | 'SOCIAL_MEDIA'
  | 'UNDEFINED';

export const createJob = (data: JobPublicationApi): Job => {
  const jobOpening = createJobOpening(data.jobOpening);
  const startDate = data.startDate ? dayjs(data.startDate).toDate() : undefined;

  const keywords = [
    data.position,
    data.subtitle,
    data.pageTitle,
    data.pageDescription,
    data.introduction,
    data.tasks,
    data.profile,
    data.weOffer,
    data.closingText,
    data.jobOpening.reference,
    data.jobOpening.contractPeriod?.name,
    data.jobOpening.orgUnit.name,
    data.jobOpening.company?.name,
    data.jobOpening.categories.map((category) => {
      return category.name;
    }),
    data.jobOpening.targetGroups.map((targetGroup) => {
      return targetGroup.name;
    }),
    data.jobOpening.workingTimes.map((workingTime) => {
      return workingTime.name;
    }),
    data.jobOpening.locations.map((location) => {
      return location.name;
    }),
    data.jobOpening.locations.map((location) => {
      return location.country?.name;
    }),
  ]
    .join(' ')
    .toLowerCase()
    .split(' ');
  return {
    id: data.id,
    language: data.language,
    position: data.position,
    pageTitle: data.pageTitle,
    subtitle: data.subtitle,
    jobPublicationURL: data.jobPublicationURL,
    applicationFormURL: data.applicationFormURL,
    applicationApplyApiURL: data.applicationApplyApiURL,
    startDate,
    endDate: data.endDate ? dayjs(data.endDate).toDate() : undefined,
    introduction: data.introduction,
    tasks: data.tasks,
    profile: data.profile,
    weOffer: data.weOffer,
    closingText: data.closingText,
    images:
      data.images &&
      data.images.map((image) => {
        let type: ImageType;
        switch (image.type) {
          case 'JOB_PUBLICATION_PAGE_TOP_LEFT':
            type = 'JOB_PUBLICATION_PAGE_TOP_LEFT';
            break;
          case 'JOB_PUBLICATION_PAGE_TOP_CENTER':
            type = 'JOB_PUBLICATION_PAGE_TOP_CENTER';
            break;
          case 'JOB_PUBLICATION_PAGE_TOP_RIGHT':
            type = 'JOB_PUBLICATION_PAGE_TOP_RIGHT';
            break;
          case 'JOB_PUBLICATION_PAGE_PANORAMA':
            type = 'JOB_PUBLICATION_PAGE_PANORAMA';
            break;
          case 'JOB_PUBLICATION_PAGE_BOTTOM_CENTER':
            type = 'JOB_PUBLICATION_PAGE_BOTTOM_CENTER';
            break;
          case 'JOB_PUBLICATION_PAGE_BOTTOM_RIGHT':
            type = 'JOB_PUBLICATION_PAGE_BOTTOM_RIGHT';
            break;
          case 'BACKGROUND':
            type = 'BACKGROUND';
            break;
          case 'SOCIAL_MEDIA':
            type = 'SOCIAL_MEDIA';
            break;
          default:
            type = 'UNDEFINED';
        }
        return {
          type,
          url: image.url,
          additionalImages:
            image.additionalImages &&
            image.additionalImages.map((additionalImage) => {
              return { url: additionalImage.url };
            }),
        };
      }),
    renderedContent: data.renderedContent && {
      contact: {
        body: data.renderedContent.contact.body,
        headline: data.renderedContent.contact.headline,
      },
      introduction: {
        body: data.renderedContent.introduction.body,
        headline: data.renderedContent.introduction.headline,
      },
      position: {
        body: data.renderedContent.position.body,
      },
      profile: {
        body: data.renderedContent.profile.body,
        headline: data.renderedContent.profile.headline,
      },
      tasks: {
        body: data.renderedContent.tasks.body,
        headline: data.renderedContent.tasks.headline,
      },
      weOffer: {
        body: data.renderedContent.weOffer.body,
        headline: data.renderedContent.weOffer.headline,
      },
      additionalInformation: {
        body: data.renderedContent.additionalInformation.body,
      },
    },
    jobOpening,
    benefits:
      data.benefits &&
      data.benefits.map((benefit) => {
        return {
          icon: benefit.icon,
          internalName: benefit.internalName,
          name: benefit.name,
          description: benefit.description,
        };
      }),
    sortDate: (data.startDate && dayjs(data.startDate).toDate()) || dayjs(data.jobOpening.createdDate).toDate(),
    keywords,
    search: keywords.join(' '),
  };
};
